import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import despesasService from '../../services/despesasService';

const formatBRL = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const EMPTY = { titulo: '', valor: '' };

export default function DespesasLote({ loteId, organizadorId, loteStatus, fluxo }) {
  const [despesas, setDespesas]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [modal, setModal]         = useState(false);
  const [editando, setEditando]   = useState(null); // despesa sendo editada
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]       = useState(false);
  const [erro, setErro]           = useState('');

  const faturado = loteStatus === 'FATURADO';
  const separada = fluxo === 'separada';
  const cavalo = fluxo === 'cavalo';

  useEffect(() => {
    if (loteId && organizadorId) carregar();
  }, [loteId, organizadorId]);

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await despesasService.listar(organizadorId, loteId);
      setDespesas(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const abrirNova = () => {
    setEditando(null);
    setForm(EMPTY);
    setErro('');
    setModal(true);
  };

  const abrirEditar = (d) => {
    setEditando(d);
    setForm({ titulo: d.titulo, valor: String(d.valor) });
    setErro('');
    setModal(true);
  };

  const fechar = () => setModal(false);

  const salvar = async () => {
    if (!form.titulo.trim()) { setErro('Informe o título.'); return; }
    const valor = parseFloat(form.valor);
    if (isNaN(valor) || valor <= 0) { setErro('Informe um valor válido.'); return; }
    setSaving(true);
    try {
      if (editando) {
        await despesasService.atualizar(organizadorId, loteId, editando.id, { titulo: form.titulo, valor });
      } else {
        await despesasService.criar(organizadorId, loteId, { titulo: form.titulo, valor });
      }
      await carregar();
      fechar();
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao salvar despesa.');
    } finally {
      setSaving(false);
    }
  };

  const excluir = async (d) => {
    if (!confirm(`Excluir despesa "${d.titulo}"?`)) return;
    try {
      await despesasService.excluir(organizadorId, loteId, d.id);
      await carregar();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro ao excluir.');
    }
  };

  const total = despesas.reduce((s, d) => s + Number(d.valor), 0);

  return (
    <div className="space-y-4">
      {/* Aviso de fluxo — a regra de despesas muda de verdade só entre "vendedor arca"
          (separada/cavalo) e "some pra comissão do comprador" (primeira_parcela/
          parcelada/gta_nota, que tratam despesas do mesmo jeito no backend). */}
      {cavalo ? (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span>
            <b>Fluxo Cavalo.</b> As despesas deste lote são descontadas do(s) vendedor(es) na
            Gestão Financeira (rateadas pela % de participação de cada um, se houver mais de um dono).
          </span>
        </div>
      ) : separada ? (
        <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span>
            <b>Fluxo: Comissão Separada.</b> As despesas deste lote são de responsabilidade do
            vendedor e aparecerão no mapa do vendedor.
          </span>
        </div>
      ) : (
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span>
            <b>Despesas do lote.</b> São acrescidas à comissão cobrada do comprador e descontadas
            do valor repassado ao vendedor.
          </span>
        </div>
      )}

      {/* Aviso faturado */}
      {faturado && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertTriangle size={16} className="flex-shrink-0" />
          Lote faturado. Desfaça a negociação para gerenciar despesas.
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-800">Despesas do Lote</h3>
        {!faturado && (
          <button
            onClick={abrirNova}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={15} /> Nova Despesa
          </button>
        )}
      </div>

      {/* Tabela */}
      {loading ? (
        <div className="text-center py-8 text-gray-400">Carregando...</div>
      ) : despesas.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">Nenhuma despesa cadastrada.</div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Título</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">Valor</th>
                {!faturado && <th className="px-4 py-2 w-20"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {despesas.map(d => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-800">{d.titulo}</td>
                  <td className="px-4 py-2 text-right font-medium text-gray-800">{formatBRL(d.valor)}</td>
                  {!faturado && (
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => abrirEditar(d)}
                          className="p-1 text-gray-400 hover:text-blue-600 rounded transition-colors">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => excluir(d)}
                          className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 border-t border-gray-200">
              <tr>
                <td className="px-4 py-2 font-semibold text-gray-700">TOTAL</td>
                <td className="px-4 py-2 text-right font-bold text-gray-900">{formatBRL(total)}</td>
                {!faturado && <td />}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editando ? 'Editar Despesa' : 'Nova Despesa'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
                <input
                  type="text"
                  value={form.titulo}
                  onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Taxa GTA, Nota Fiscal..."
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.valor}
                  onChange={e => setForm(f => ({ ...f, valor: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0,00"
                />
              </div>
              {erro && <p className="text-sm text-red-600">{erro}</p>}
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={fechar}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button onClick={salvar} disabled={saving}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
